import { useCallback, useMemo, useRef, useState } from 'react';
import { Box, Flex, Button, Text } from '@chakra-ui/react';
import { Avatar } from '@kula-ai/components';
import { CheckCircle, Trash01 } from '@kula-ai/icons-core';
import { EmailComposeBlock } from './EmailComposeBlock';
import type { EmailComposeBlockData } from '../../../types';

interface EmailComposeGroupProps {
  drafts: EmailComposeBlockData[];
}

interface DraftItem {
  key: string;
  draft: EmailComposeBlockData;
  name: string;
  email?: string;
}

const keyFor = (d: EmailComposeBlockData, i: number): string =>
  d.toolCallId ?? `draft-${i}`;

// Lead with the candidate's name (who you're emailing); fall back to the
// address, then the id.
const draftLabel = (d: EmailComposeBlockData, i: number): DraftItem => {
  const r = d.recipients[0];
  return {
    key: keyFor(d, i),
    draft: d,
    name: r?.name || r?.email || `Recipient ${i + 1}`,
    email: r?.name ? r.email : undefined,
  };
};

const NavRow = ({
  item,
  active,
  sent,
  onSelect,
  onRemove,
}: {
  item: DraftItem;
  active: boolean;
  sent: boolean;
  onSelect: () => void;
  onRemove: () => void;
}) => (
  <Flex
    align="center"
    gap="8px"
    px="10px"
    py="8px"
    borderRadius="8px"
    cursor="pointer"
    bg={active ? 'primary-50' : 'transparent'}
    _hover={{ bg: active ? 'primary-50' : 'secondary-50' }}
    onClick={onSelect}
    data-test-id={`agentforge-email-nav-${item.key}`}
    aria-current={active}
  >
    <Avatar name={item.name} shape="circle" size={24} />
    <Box flex="1" minW="0">
      <Text
        fontSize="sm"
        fontWeight={active ? 'semibold' : 'medium'}
        color={active ? 'primary-700' : 'secondary-900'}
        isTruncated
      >
        {item.name}
      </Text>
      {item.email && (
        <Text fontSize="xs" color="secondary-500" isTruncated>
          {item.email}
        </Text>
      )}
    </Box>
    {sent ? (
      <Box as="span" color="pastel-2-dark" display="inline-flex" flexShrink={0}>
        <CheckCircle size={16} />
      </Box>
    ) : (
      <Box
        as="button"
        type="button"
        aria-label={`Remove ${item.name} from the batch`}
        onClick={(e: React.MouseEvent) => {
          e.stopPropagation();
          onRemove();
        }}
        color="secondary-400"
        display="inline-flex"
        flexShrink={0}
        _hover={{ color: 'pastel-1-dark' }}
        data-test-id={`agentforge-email-remove-${item.key}`}
      >
        <Trash01 size={16} />
      </Box>
    )}
  </Flex>
);

// Repeater-nav layout for the per-candidate drafts produced when several
// candidates were selected: a left recipient list (select / remove) and the
// active candidate's composer on the right. Every composer stays mounted so
// switching recipients preserves in-progress edits; only the active one shows.
// A single draft renders bare (no group chrome).
export const EmailComposeGroup = ({ drafts }: EmailComposeGroupProps) => {
  const sendsRef = useRef<Map<string, () => void>>(new Map());
  const registerSend = useCallback((key: string, fn: (() => void) | null) => {
    if (fn) sendsRef.current.set(key, fn);
    else sendsRef.current.delete(key);
  }, []);

  const items = useMemo(() => drafts.map(draftLabel), [drafts]);

  const [removed, setRemoved] = useState<Set<string>>(new Set());
  const [sentKeys, setSentKeys] = useState<Set<string>>(
    () => new Set(items.filter((it) => it.draft.sent).map((it) => it.key))
  );
  const [activeKey, setActiveKey] = useState<string>(items[0]?.key ?? '');

  const visible = items.filter((it) => !removed.has(it.key));
  // Keep a valid active row even after the active one is removed.
  const active = visible.some((it) => it.key === activeKey)
    ? activeKey
    : (visible[0]?.key ?? '');

  const handleSent = useCallback((key: string) => {
    setSentKeys((prev) => new Set(prev).add(key));
  }, []);

  const removeDraft = (key: string) => {
    setRemoved((prev) => new Set(prev).add(key));
    sendsRef.current.delete(key);
  };

  if (drafts.length <= 1) {
    return drafts[0] ? <EmailComposeBlock data={drafts[0]} /> : null;
  }

  const total = visible.length;
  const sentCount = visible.filter((it) => sentKeys.has(it.key)).length;
  const allSent = total > 0 && sentCount >= total;

  const sendAll = () => {
    visible.forEach((it) => {
      if (!sentKeys.has(it.key)) sendsRef.current.get(it.key)?.();
    });
  };

  return (
    <Box data-test-id="agentforge-email-compose-group">
      <Flex align="center" justify="space-between" mb="10px" px="4px" gap="8px">
        <Text fontSize="sm" fontWeight="semibold" color="secondary-900">
          {total} personalized draft{total === 1 ? '' : 's'}
          {sentCount > 0 ? ` · ${sentCount}/${total} sent` : ''}
        </Text>
        <Button
          variant="primary"
          size="sm"
          isDisabled={allSent || total === 0}
          onClick={sendAll}
        >
          {allSent ? 'All sent' : `Send all (${total - sentCount})`}
        </Button>
      </Flex>

      <Flex
        direction={{ base: 'column', lg: 'row' }}
        gap="12px"
        align="stretch"
      >
        <Flex
          direction="column"
          gap="2px"
          flexShrink={0}
          w={{ base: '100%', lg: '220px' }}
          p="6px"
          border="1px solid"
          borderColor="secondary-100"
          borderRadius="10px"
          bg="base-white"
          maxH={{ lg: '520px' }}
          overflowY={{ lg: 'auto' }}
        >
          {visible.map((it) => (
            <NavRow
              key={it.key}
              item={it}
              active={it.key === active}
              sent={sentKeys.has(it.key)}
              onSelect={() => setActiveKey(it.key)}
              onRemove={() => removeDraft(it.key)}
            />
          ))}
        </Flex>

        <Box flex="1" minW="0">
          {visible.map((it) => (
            <Box key={it.key} display={it.key === active ? 'block' : 'none'}>
              <EmailComposeBlock
                data={it.draft}
                registerSend={registerSend}
                onSent={() => handleSent(it.key)}
              />
            </Box>
          ))}
        </Box>
      </Flex>
    </Box>
  );
};
